import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../utils/api';
import { ApiResponse } from '../types/api';
import { RealtimeKind } from './realtimeKinds';

// Detection automation: a project rule that says "when a detection of this
// kind opens an incident here, simulate a fire from it under current
// conditions for N hours and publish the result to the organisation's GIS".
// Project Settings -> Automatic simulations edits the rules; each firing is
// a run, shown there and on the incident page.

export type AutomationRunStatus =
  | 'QUEUED'
  | 'SIMULATING'
  | 'PUBLISHING'
  | 'DONE'
  | 'FAILED'
  | 'SKIPPED';

export type AutomationTrigger =
  | 'new_incident'
  | 'escalated'
  | 'rekindle'
  | 'reopened'
  | 'secondary'
  | 'manual';

export interface AutomationRule {
  id: number;
  project_id: number;
  kind: RealtimeKind;
  enabled: boolean;
  /** Hours simulated from the moment the incident opens. */
  duration_hours: number;
  /** Hazards only: the agency alert level (1 advice .. 3 emergency warning)
   *  a detection must reach before it is simulated. */
  min_level: number;
  publish_to_gis: boolean;
  /** Minimum spacing between two runs for the same fire. */
  cooldown_minutes: number;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  last_triggered_at: string | null;
  trigger_count: number;
}

export interface AutomationRunGisLayer {
  id: number;
  partition: string;
  /** The fire's id in the GIS (`sim_<id>_p<n>`). */
  fire: string;
  status: string;
  error: string | null;
}

export interface AutomationRun {
  id: number;
  incident_id: number;
  project_id: number;
  rule_id: number | null;
  kind: string;
  trigger: AutomationTrigger;
  status: AutomationRunStatus;
  detail: string;
  duration_hours: number;
  publish_to_gis: boolean;
  ignition: {
    mode?: 'points' | 'hull' | 'perimeter' | 'none';
    count?: number;
    detections?: number;
    outside?: number;
    clipped?: boolean;
  };
  simulation: { id: string; name: string; status: string } | null;
  gis: AutomationRunGisLayer[];
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface AutomationRulesResponse {
  rules: AutomationRule[];
  /** The project's most recent firings, newest first. */
  runs: AutomationRun[];
  /** Whether this user may add, edit or remove rules (organisation staff). */
  can_edit: boolean;
  min_duration_hours: number;
  max_duration_hours: number;
  /** Detection kinds live for this organisation; a rule for any other kind can never fire. */
  kinds: Record<string, boolean>;
  gis_configured: boolean;
}

export type AutomationRuleInput = Partial<
  Pick<
    AutomationRule,
    'kind' | 'enabled' | 'duration_hours' | 'min_level' | 'publish_to_gis' | 'cooldown_minutes'
  >
>;

export const isRunBusy = (run: AutomationRun): boolean =>
  run.status === 'QUEUED' || run.status === 'SIMULATING' || run.status === 'PUBLISHING';

const key = (projectId: string | undefined) => ['project', projectId, 'automation-rules'];

export const useProjectAutomationRules = (projectId: string | undefined) =>
  useQuery({
    queryKey: key(projectId),
    queryFn: async () =>
      (
        await api.request<ApiResponse<AutomationRulesResponse>>(
          `/api/projects/${projectId}/automation-rules`
        )
      ).data,
    enabled: !!projectId,
    refetchOnWindowFocus: false,
    // Follow runs in flight (a simulation takes minutes, a publish seconds).
    refetchInterval: (query) =>
      (query.state.data?.runs ?? []).some(isRunBusy) ? 15_000 : false,
  });

export const useCreateAutomationRule = (projectId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AutomationRuleInput) =>
      api.request<ApiResponse<AutomationRule>>(`/api/projects/${projectId}/automation-rules`, {
        method: 'POST',
        body: input,
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key(projectId) }),
  });
};

export const useUpdateAutomationRule = (projectId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: AutomationRuleInput & { id: number }) =>
      api.request<ApiResponse<AutomationRule>>(
        `/api/projects/${projectId}/automation-rules/${id}`,
        { method: 'PATCH', body: input }
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key(projectId) }),
  });
};

export const useDeleteAutomationRule = (projectId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.request<void>(`/api/projects/${projectId}/automation-rules/${id}`, {
        method: 'DELETE',
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key(projectId) }),
  });
};
